import { useMemo, useState } from "react"
import { Box, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { faCalendarDays } from "@fortawesome/pro-solid-svg-icons"
import { ErrorBoundary } from "react-error-boundary"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { tailwindPalette } from "../../../../../../theme/tailwindPalette"
import { EmptyContent, EmptyContentVariant } from "../../../../../../../shared/components/skeleton/EmptyContent"
import { DatePeriod } from "../../../../../../../shared/components/filters/selectDatePeriodFilter/SelectDatePeriodFilter"
import { UserDashboardErrorFallbackCard } from "../../UserDashboardErrorFallbackCard"
import { ReadonlyOrder } from "../../../weekMenuTypes"
import { UserMeetingService } from "../../../../upcomingOrders/meetingService/page/MeetingService"

import { useListPagination } from "../../../../../../../shared/hooks/useListPagination"
import { MeetingListPagination } from "../../../../../../serviceCenter/report/home/components/meetigs/MeetingListPagination"
import { UpcomingOrdersPanel } from "../UpcomingOrdersPanel"
import { UpcomingOrdersModal } from "../UpcomingOrdersModal"
import { MeetingRow } from "./MeetingRow"

const useStyles = makeStyles()((theme) => ({
    rowsContainer: {
        display: "flex",
        flexDirection: "column",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    dayLabel: {
        color: theme.palette.tailwind.slate[500],
        fontSize: "1.25rem",
        fontWeight: 500,
        marginBottom: ".5rem",
        "&:not(:first-of-type)": {
            marginTop: "2rem",
        },
    },
    errorCard: {
        backgroundColor: "transparent",
        boxShadow: "none",
    },
    errorContent: {
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
    },
}))

type MeetingsPanelProps = Readonly<{
    meetings: ReadonlyOrder[]
    period: DatePeriod
}>

type DayGroup = Readonly<{
    key: string
    label: string
    meetings: ReadonlyOrder[]
}>

export function MeetingsPanel({ meetings, period }: MeetingsPanelProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const { pagedItems, pagination } = useListPagination(meetings)

    const groups = useMemo<DayGroup[]>(() => {
        const todayIso = dateTimeFactory.now().toISODate()
        const tomorrowIso = dateTimeFactory.now().plus({ days: 1 }).toISODate()
        const byKey = new Map<string, DayGroup>()

        for (const meeting of pagedItems) {
            const startStr = meeting.meetingDetails.startDateTime || meeting.deliveryDetails.time
            const start = startStr ? dateTimeFactory.fromApi(startStr).startOf("day") : null
            const startIso = start?.isValid ? start.toISODate() : null
            const key = startIso ?? `unknown-${meeting.id}`
            const label =
                startIso === todayIso
                    ? l10n.getString("user-home-meeting-day-today")
                    : startIso === tomorrowIso
                      ? l10n.getString("user-home-meeting-day-tomorrow")
                      : start?.isValid
                        ? start.toFormat("d MMMM yyyy")
                        : l10n.getString("user-home-meeting-day-unknown")

            const existing = byKey.get(key)
            if (existing) {
                byKey.set(key, { ...existing, meetings: [...existing.meetings, meeting] })
            } else {
                byKey.set(key, { key, label, meetings: [meeting] })
            }
        }

        return Array.from(byKey.values())
    }, [pagedItems, dateTimeFactory, l10n])

    return (
        <>
            <UpcomingOrdersPanel
                icon={faCalendarDays}
                iconBackgroundColor={tailwindPalette.blue[100]}
                iconColor={tailwindPalette.blue[400]}
                titleId="user-home-upcoming-meetings"
                titleFallback="Meetings"
                counter={pagination.totalItems}
                onShowAll={() => setIsModalOpen(true)}
                collapseAriaLabel={l10n.getString("user-home-meetings-collapse")}
                expandAriaLabel={l10n.getString("user-home-meetings-expand")}
                footer={pagination.totalItems > 0 ? <MeetingListPagination {...pagination} /> : undefined}
            >
                {!groups.length ? (
                    <LocalizedStrict id="user-home-upcoming-meetings-no-meetings" attrs={{ title: true }}>
                        <EmptyContent variant={EmptyContentVariant.SINGLE_LEFT} title="No meetings in period" />
                    </LocalizedStrict>
                ) : (
                    <Box className={classes.rowsContainer}>
                        {groups.flatMap((group) => [
                            <Typography key={`label-${group.key}`} variant="h6" className={classes.dayLabel}>
                                {group.label}
                            </Typography>,
                            ...group.meetings.map((meeting) => (
                                <ErrorBoundary
                                    key={meeting.id}
                                    fallback={
                                        <UserDashboardErrorFallbackCard
                                            classNames={classes.errorCard}
                                            contentClassNames={classes.errorContent}
                                        />
                                    }
                                >
                                    <MeetingRow meeting={meeting} meetings={meetings} />
                                </ErrorBoundary>
                            )),
                        ])}
                    </Box>
                )}
            </UpcomingOrdersPanel>
            {isModalOpen && (
                <LocalizedStrict id="user-home-upcoming-meetings-modal-title" attrs={{ title: true }}>
                    <UpcomingOrdersModal open onClose={() => setIsModalOpen(false)} title="Meetings">
                        <UserMeetingService initialPeriod={period} />
                    </UpcomingOrdersModal>
                </LocalizedStrict>
            )}
        </>
    )
}
